// One source of truth for the password policy we SHOW and the policy we
// ENFORCE client-side.
//
// This used to be duplicated three ways — inline in SignUpPage, inline in
// ResetPasswordPage, and again in shared/components/PasswordRequirements —
// which is how the rules drifted apart (the shared copy still advertised a
// old "last 5 passwords" rule that Identity Platform does not enforce).
// Anything that asks a user to choose a password should import from here.
import { Check, Info, X } from 'lucide-react';

export interface PasswordRule {
  label: string;
  test: (value: string) => boolean;
}

/** Rules we can check in the browser as the user types. */
export const PASSWORD_RULES: ReadonlyArray<PasswordRule> = [
  { label: 'At least 10 characters', test: (v) => v.length >= 10 },
  { label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'One number', test: (v) => /[0-9]/.test(v) },
  { label: 'One special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

/**
 * Rules the SERVER enforces and the browser cannot pre-check, so they are
 * listed for transparency rather than ticked off. Identity Platform rejects
 * passwords found in known breach corpora at sign-up time.
 */
export const PASSWORD_SERVER_RULES: ReadonlyArray<string> = [
  'Must not be a commonly compromised password',
];

/** Whether `value` satisfies every client-checkable rule. */
export function passwordMeetsPolicy(value: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(value));
}

interface PasswordChecklistProps {
  value: string;
  /** Font stack of the surrounding auth screen. */
  fontFamily?: string;
}

/**
 * The requirements panel itself. Renders as guidance — each line greys →
 * greens as it is satisfied — never as an error, and never behind a hover.
 * Callers decide WHEN to show it (typically: field focused or non-empty).
 */
export function PasswordChecklist({ value, fontFamily }: PasswordChecklistProps) {
  return (
    <ul
      className="flex flex-col gap-[6px] text-[13px] rounded-lg border border-border bg-muted p-3 w-full"
      style={fontFamily ? { fontFamily } : undefined}
    >
      {PASSWORD_RULES.map(({ label, test }) => {
        const ok = test(value);
        return (
          <li
            key={label}
            className={`flex items-center gap-[8px] ${ok ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}
          >
            {ok ? (
              <Check className="w-[14px] h-[14px] shrink-0" />
            ) : (
              <X className="w-[14px] h-[14px] shrink-0" />
            )}
            {label}
          </li>
        );
      })}
      {PASSWORD_SERVER_RULES.map((label) => (
        <li key={label} className="flex items-center gap-[8px] text-muted-foreground">
          <Info className="w-[14px] h-[14px] shrink-0" />
          {label}
        </li>
      ))}
    </ul>
  );
}
